/*
 * Liquid contract: <andcharlie-size-finder data-chart-anchor="#chart-id">
 *   <script type="application/json" data-ac-size-payload>{system,preference,product}</script>
 *   <form data-ac-fit-form><div data-ac-fit-body-fields>weightKg + shortBody</div>
 *     <div data-ac-fit-measurements></div><button type="submit">…</button></form>
 *   <div data-ac-fit-result aria-live="polite"></div>
 * </andcharlie-size-finder>
 * The engine alone chooses candidates and rule-dependent questions. The owner
 * choosing unknown reveals the requested auxiliary length UI without inferring
 * body shape or a numerical boundary. Profiles contain dog inputs, never product
 * rankings. Optional account transport is disabled until its authenticated app is installed.
 */
if (!customElements.get('andcharlie-size-finder')) {
  customElements.define('andcharlie-size-finder', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.engineInitialized = false;
      this.profileRestored = false;
      this.form = this.querySelector('[data-ac-fit-form]');
      this.result = this.querySelector('[data-ac-fit-result]');
      this.measurements = this.querySelector('[data-ac-fit-measurements]');
      this.bodyFields = this.querySelector('[data-ac-fit-body-fields]');
      this.unknownFields = this.querySelector('[data-ac-fit-unknown-fields]');
      this.lengthOptional = this.querySelector('[data-ac-fit-length-optional]');
      this.profileNotice = this.querySelector('[data-ac-profile-notice]');
      this.identity = this.dataset.customerId || 'guest';
      this.dirty = false;
      if (!this.form || !this.result) return;

      this.result.setAttribute('aria-live', 'polite');
      this.result.setAttribute('aria-atomic', 'true');
      try {
        this.payload = JSON.parse(this.querySelector('[data-ac-size-payload]')?.textContent || 'null');
      } catch (_) {
        this.payload = null;
      }
      if (!this.payload?.system) {
        this.form.hidden = true;
        this.showFallback();
        return;
      }
      if (!this.initializeEngine()) {
        this.form.hidden = true;
        this.showFallback();
        // Dawn/Theme Editor reinject external scripts dynamically. Those scripts
        // can execute out of source order even when their defer attributes remain.
        // Retry only on an existing engine script's load event, never poll or fetch.
        document.querySelectorAll('script[src]').forEach((script) => {
          let isEngine = false;
          try { isEngine = new URL(script.src, document.baseURI).pathname.endsWith('/andcharlie-size-engine.js'); }
          catch (_) { /* Invalid script URLs cannot supply this dependency. */ }
          if (isEngine) script.addEventListener('load', () => this.initializeEngine(), {
            once: true, signal: this.controller.signal,
          });
        });
      }
    }

    initializeEngine() {
      if (this.engineInitialized) return true;
      if (!this.isConnected || this.controller.signal.aborted) return false;
      this.engine = window.AndCharlieSizeEngine;
      if (typeof this.engine?.resolveFit !== 'function') return false;
      this.engineInitialized = true;
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.form.addEventListener('input', (event) => this.inputChanged(event), options);
      this.form.addEventListener('change', (event) => this.inputChanged(event), options);
      this.form.addEventListener('focusin', () => this.start(), options);
      this.result.replaceChildren();
      this.prepareForm();
      this.restoreProfile();
      document.querySelectorAll('script[src]').forEach((script) => {
        if (script.src.includes('/andcharlie-pet-profile.js')) script.addEventListener('load', () => this.restoreProfile(), {once:true, signal:this.controller.signal});
      });
      return true;
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    prepareForm() {
      // An empty profile can reveal only the engine's explicit data/selection state.
      // Do not treat a missing initial answer as a broken configuration.
      const initial = this.resolve({});
      if (initial?.status === 'no_size') {
        this.hidden = true;
        return;
      }
      if (['invalid', 'no_match'].includes(initial?.status) || !initial ||
          !this.engine.validateSystem(this.payload.system).valid ||
          (this.payload.product && !this.engine.validateProduct(this.payload.system, this.payload.product).valid)) {
        this.form.hidden = true;
        this.showFallback();
        return;
      }
      this.hidden = false;
      this.form.hidden = false;
      const measurementBased = this.payload.system.method === 'measurement';
      if (this.bodyFields) {
        this.bodyFields.hidden = measurementBased;
        this.bodyFields.querySelectorAll('input').forEach((input) => {
          input.disabled = measurementBased;
        });
      }
      this.syncUnknownFields();
      if (measurementBased) {
        if (initial.status !== 'needs_input' || !this.showQuestions(initial.requestedFields, false)) {
          this.form.hidden = true;
          this.showFallback();
        }
      }
    }

    resolve(profile) {
      try {
        return this.engine.resolveFit(profile, this.payload.system, this.payload.preference, this.payload.product);
      } catch (_) {
        return null;
      }
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.dispatchEvent(new CustomEvent('ac:fit-start', {
        bubbles: true,
        detail: {
          systemKey: this.payload.system.key,
          ruleRevision: this.payload.system.revision || null,
        },
      }));
    }

    inputChanged(event) {
      if (!event.target.matches('input')) return;
      this.start();
      this.dirty = true;
      this.result.replaceChildren();
      this.highlight();
      if (['weightKg', 'shortBody'].includes(event.target.name)) {
        this.measurements?.replaceChildren();
        const length = this.unknownFields?.querySelector('input');
        if (length) length.required = false;
        if (this.lengthOptional) this.lengthOptional.hidden = false;
        this.syncUnknownFields();
      }
    }

    syncUnknownFields() {
      if (!this.unknownFields) return;
      const unknown = this.payload.system.method === 'body' && new FormData(this.form).get('shortBody') === 'unknown';
      this.unknownFields.hidden = !unknown;
      const input = this.unknownFields.querySelector('input');
      if (input) {
        input.disabled = !unknown;
        if (!unknown) input.required = false;
      }
      if (!unknown && this.lengthOptional) this.lengthOptional.hidden = false;
    }

    profile() {
      const data = new FormData(this.form);
      const profile = {};
      for (const field of ['weightKg', 'lengthCm', 'chestCm', 'neckCm']) {
        const value = data.get(field);
        if (typeof value === 'string' && value.trim() !== '') profile[field] = this.engine.parseNumber(value);
      }
      if (data.has('shortBody')) profile.shortBody = data.get('shortBody');
      return profile;
    }

    submit(event) {
      event.preventDefault();
      this.start();
      this.highlight();
      if (!this.form.reportValidity()) return;
      const profile = this.profile();
      const resolved = this.resolve(profile);
      if (resolved?.status === 'needs_input') {
        this.result.replaceChildren();
        if (!this.showQuestions(resolved.requestedFields, true)) this.showFallback();
        return;
      }
      if (resolved?.status !== 'ready' || !this.showResult(resolved)) {
        this.showFallback(resolved);
      }
      if (this.payload.system.method === 'body' && !['invalid', 'no_match'].includes(resolved?.status)) {
        this.rememberProfile(profile);
      }
    }

    sessionStorage() { try { return window.sessionStorage; } catch (_) { return null; } }

    restoreProfile() {
      if (this.payload.system.method !== 'body' || this.form.hidden) return;
      const profiles = window.AndCharliePetProfile;
      if (!profiles || this.profileRestored) return;
      this.profileRestored = true;
      const saved = profiles?.read(this.sessionStorage(), this.identity);
      if (saved) this.fillProfile(saved, 'このタブで入力した情報を使用しています。');
      if (this.dataset.accountEnabled !== 'true' || this.identity === 'guest') return;
      fetch('/apps/andcharlie-fit/profile', {credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal})
        .then((response) => { if (!response.ok) throw Error('Profile unavailable'); return response.json(); })
        .then((entry) => {
          this.accountCsrf = entry.csrf;
          const profile = profiles?.clean(entry.profile?.inputs);
          if (profile && !this.dirty && this.isConnected) this.fillProfile(profile, 'アカウントのうちの子情報を使用しています。');
        }).catch(() => { /* The current form and session remain usable. */ });
    }

    fillProfile(profile, notice) {
      if (this.dirty) return;
      this.measurements?.replaceChildren();
      this.highlight();
      this.result.replaceChildren();
      for (const input of this.form.querySelectorAll('input')) {
        if (input.type === 'radio') input.checked = input.value === profile[input.name];
        else if (input.type !== 'checkbox') input.value = profile[input.name] ?? '';
      }
      this.syncUnknownFields();
      // Ask only fields required by this revision, then restore their saved values.
      let result = this.resolve(this.profile());
      for (let step = 0; step < 3 && result?.status === 'needs_input'; step += 1) {
        if (!this.showQuestions(result.requestedFields, false)) break;
        for (const input of this.form.querySelectorAll('input')) {
          if (!['radio', 'checkbox'].includes(input.type) && profile[input.name] != null) input.value = profile[input.name];
        }
        result = this.resolve(this.profile());
      }
      if (result?.status === 'ready') this.showResult(result);
      else if (result?.status === 'no_rules') this.showFallback();
      this.setProfileNotice(notice);
      const details = this.closest('details');
      if (details) details.open = true;
    }

    setProfileNotice(message) {
      if (!this.profileNotice) return;
      this.profileNotice.replaceChildren();
      const text = document.createElement('span');
      text.textContent = message;
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'link underlined-link';
      clear.textContent = '入力をクリア';
      clear.addEventListener('click', () => {
        window.AndCharliePetProfile?.clear(this.sessionStorage());
        this.dirty = true;
        this.form.reset();
        this.measurements?.replaceChildren();
        this.syncUnknownFields();
        this.result.replaceChildren();
        this.highlight();
        this.profileNotice.replaceChildren();
      }, {signal: this.controller.signal});
      this.profileNotice.append(text, clear);
    }

    rememberProfile(profile) {
      if (window.AndCharliePetProfile?.write(this.sessionStorage(), this.identity, profile)) {
        this.setProfileNotice('このタブの閲覧中は、入力した情報を使えます。');
      }
      if (this.dataset.accountEnabled !== 'true' || this.identity === 'guest') return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'link underlined-link';
      button.textContent = 'うちの子情報をアカウントに保存';
      const save = async () => {
        button.disabled = true;
        try {
          // Renew the short-lived CSRF token before every save; never retry a write blindly.
          const fresh = await fetch('/apps/andcharlie-fit/profile', {credentials:'same-origin',cache:'no-store',signal:this.controller.signal});
          if (!fresh.ok) throw Error('Profile unavailable');
          this.accountCsrf = (await fresh.json()).csrf;
          if (!this.accountCsrf) throw Error('Profile authentication unavailable');
          const response = await fetch('/apps/andcharlie-fit/profile', {method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'}, signal: this.controller.signal,
            body: JSON.stringify({inputs: profile, csrf: this.accountCsrf})});
          if (!response.ok) throw Error('Profile save failed');
          const entry = await response.json();
          if (!window.AndCharliePetProfile.clean(entry.profile?.inputs)) throw Error('Invalid saved profile');
          this.setProfileNotice('うちの子情報をアカウントに保存しました。');
        } catch (_) {
          button.disabled = false;
          button.textContent = '保存できませんでした。再試行する';
        }
      };
      button.addEventListener('click', save, {signal: this.controller.signal});
      this.result.append(button);
      if (this.querySelector('[data-ac-profile-save]')?.checked) save();
    }

    highlight(resolved) {
      const anchor = this.dataset.chartAnchor;
      if (!anchor?.startsWith('#')) return;
      const chart = document.getElementById(anchor.slice(1));
      chart?.querySelectorAll('[data-ac-size-key]').forEach((row) => {
        row.removeAttribute('data-ac-fit-rank');
        row.querySelector('[data-ac-fit-row-label]')?.remove();
        if (!resolved?.baseCandidates?.includes(row.dataset.acSizeKey)) return;
        const preferred = resolved.preferredKey === row.dataset.acSizeKey || resolved.baseCandidates.length === 1;
        row.dataset.acFitRank = preferred ? 'primary' : resolved.preferredKey ? 'comparison' : 'equal';
        const label = document.createElement('span');
        label.dataset.acFitRowLabel = '';
        label.className = 'ac-size-foundation__candidate-label';
        label.textContent = preferred ? 'おすすめ' : resolved.preferredKey ? '候補' : '同じく候補';
        row.querySelector('th')?.append(label);
      });
    }

    showQuestions(fields, focus) {
      const labels = { lengthCm: '着丈', chestCm: '胴回り', neckCm: '首回り' };
      if (!this.measurements || !Array.isArray(fields) || fields.length === 0 ||
          fields.some((field) => !Object.prototype.hasOwnProperty.call(labels, field))) return false;
      let firstInput;
      for (const field of new Set(fields)) {
        let input = field === 'lengthCm' && this.payload.system.method === 'body' ? this.unknownFields?.querySelector('input') : null;
        if (input) {
          // A body-length question is available only after the owner's unknown answer.
          if (input.disabled) return false;
          input.required = true;
          if (this.lengthOptional) this.lengthOptional.hidden = true;
        }
        const existing = this.form.querySelector(`[name="${field}"]`);
        input ||= existing && !existing.disabled ? existing : null;
        if (!input) {
          const label = document.createElement('label');
          label.className = 'ac-size-finder__step';
          const title = document.createElement('span');
          title.className = 'ac-size-finder__label';
          title.textContent = labels[field];
          const number = document.createElement('span');
          number.className = 'ac-size-finder__number';
          input = document.createElement('input');
          input.type = 'text';
          input.name = field;
          input.min = '0';
          input.step = 'any';
          input.inputMode = 'decimal';
          input.required = true;
          input.autocomplete = 'off';
          const unit = document.createElement('span');
          unit.className = 'ac-size-finder__unit';
          unit.textContent = 'cm';
          number.append(input, unit);
          label.append(title, number);
          this.measurements.append(label);
        }
        input.required = true;
        firstInput ||= input;
      }
      if (focus) {
        this.appendText('追加の採寸値を入力してください。');
        firstInput?.focus();
      }
      return true;
    }

    labelFor(row) {
      const values = this.payload.product?.optionValues;
      if (!Array.isArray(values)) return row.label;
      const matches = (row.optionValues || []).filter((value) => values.includes(value));
      return matches.length === 1 ? matches[0] : null;
    }

    showResult(resolved) {
      const rows = this.payload.system.chart?.rows;
      const keys = resolved.rankedCandidates;
      const base = resolved.baseCandidates;
      if (!Array.isArray(rows) || !Array.isArray(keys) || !Array.isArray(base) ||
          keys.length < 1 || keys.length > 2 || new Set(keys).size !== keys.length ||
          keys.some((key) => !base.includes(key))) return false;
      const candidates = keys.map((key) => rows.find((row) => row.key === key));
      if (candidates.some((row) => !row)) return false;
      const labels = candidates.map((row) => this.labelFor(row));
      if (labels.some((label) => typeof label !== 'string' || label.trim() === '')) return false;
      const preferredIndex = resolved.preferredKey == null ? -1 : keys.indexOf(resolved.preferredKey);
      if (resolved.preferredKey != null && preferredIndex < 0) return false;

      this.result.replaceChildren();
      const title = preferredIndex >= 0 || labels.length === 1 ? 'おすすめサイズ' : 'サイズ候補';
      this.appendText(title, 'ac-size-finder__result-label');
      this.appendText(preferredIndex >= 0 ? labels[preferredIndex] : labels.join(' / '), 'ac-size-finder__summary');
      if (preferredIndex >= 0 && labels.length === 2) this.appendText(`もうひとつの候補：${labels[1 - preferredIndex]}`, 'ac-size-finder__secondary');
      this.highlight(resolved);
      this.appendChartLink();

      // Stock is a separate, read-only display of the same candidates.
      const availability = this.payload.product?.availableByLabel;
      const unavailable = labels.filter((label) => availability?.[label] === false);
      if (unavailable.length) {
        this.appendText(`${unavailable.join(' / ')}：現在在庫切れです。`, 'ac-size-finder__availability');
      }
      this.dispatchEvent(new CustomEvent('ac:fit-result', {
        bubbles: true,
        detail: {
          systemKey: resolved.systemKey,
          ruleRevision: resolved.revision,
          candidateKeys: [...keys],
          baseCandidateKeys: [...base],
          preferredKey: resolved.preferredKey || null,
        },
      }));
      return true;
    }

    appendText(text, className) {
      const paragraph = document.createElement('p');
      if (className) paragraph.className = className;
      paragraph.textContent = text;
      this.result.append(paragraph);
    }

    appendChartLink() {
      const anchor = this.dataset.chartAnchor;
      if (typeof anchor !== 'string' || !anchor.startsWith('#') || anchor.length < 2) return;
      const link = document.createElement('a');
      link.href = anchor;
      link.className = 'link underlined-link';
      link.textContent = 'サイズ表を見る';
      link.addEventListener('click', () => {
        const chart = document.getElementById(anchor.slice(1));
        const details = chart?.closest('details');
        if (details) details.open = true;
      }, { signal: this.controller.signal });
      this.result.append(link);
    }

    showFallback(resolved) {
      this.highlight();
      this.result.replaceChildren();
      if (resolved?.reason === 'invalid_input') {
        const labels = {weightKg:'体重',neckCm:'首回り',chestCm:'胴回り',lengthCm:'着丈'};
        this.appendText(`${labels[resolved.invalidField] || '入力値'}を正しい数値で入力してください。例：13.5、１３、５`);
        return;
      }
      if (resolved?.status === 'no_match' || resolved?.reason === 'input_out_of_bounds') {
        this.appendText(resolved.reason === 'body_shape_unresolved' ? '体型を確認してサイズをご案内します。ご相談ください。' : '個別にサイズをご案内します。入力した数値を添えてご相談ください。');
        const link = document.createElement('a');
        link.href = this.dataset.contactUrl || '/pages/contact-us';
        link.className = 'link underlined-link';
        link.textContent = 'サイズについて相談する';
        this.result.append(link);
        return;
      }
      this.appendText('サイズ表をご確認ください。');
      this.appendChartLink();
    }
  });
}
